import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import { TimeSelect } from '../common/DateTimePicker';

const PATTERN_OPTIONS = [
  { value: 'daily', label: 'Todos los días' },
  { value: 'weekdays', label: 'Lunes a Viernes' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
  { value: 'specific', label: 'Día específico' },
];

const PLAY_MODE_OPTIONS = [
  { value: 'random', label: 'Aleatorio' },
  { value: 'sequential', label: 'Secuencial' },
  { value: 'weighted', label: 'Ponderado' },
];

const toHHMM = (t) => (t ? String(t).slice(0, 5) : '');

export default function RotationBlockEditor({ block, onSaved, onClose }) {
  const params = useParams();
  const navigate = useNavigate();

  const [blockId, setBlockId] = useState(block?.id || params?.id || null);
  const [blockData, setBlock] = useState({
    name: '',
    description: '',
    day_pattern: 'weekdays',
    start_time: '00:00',
    end_time: '07:00',
    play_mode: 'random',
    is_active: true,
  });
  const [shows, setShows] = useState([]);
  const [allShows, setAllShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedNotice, setSavedNotice] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedShowId, setSelectedShowId] = useState('');
  const [showWeight, setShowWeight] = useState(100);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Derive available shows from fresh state (no stale closures)
  const availableShows = useMemo(
    () => allShows.filter((s) => !shows.some((bs) => (bs.show_id || bs.id) === s.id)),
    [allShows, shows]
  );

  const reloadBlockShows = async (id) => {
    const { data } = await api.get(`/api/rotation-blocks/${id}`);
    setBlock({
      name: data.name || '',
      description: data.description || '',
      day_pattern: data.day_pattern || 'weekdays',
      start_time: toHHMM(data.start_time) || '00:00',
      end_time: toHHMM(data.end_time) || '07:00',
      play_mode: data.play_mode || 'random',
      is_active: data.is_active !== false,
    });
    setShows(data.shows || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: showsData } = await api.get('/api/shows?is_active=true');
        if (cancelled) return;
        setAllShows(showsData || []);
        if (blockId) {
          await reloadBlockShows(blockId);
        }
      } catch (err) {
        console.error('[RotationBlockEditor] Failed to load:', err);
        if (!cancelled) setError('No se pudo cargar la información del bloque');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId]);

  const close = () => {
    if (typeof onClose === 'function') {
      onClose();
    } else {
      navigate('/rotation-blocks');
    }
  };

  const notifySaved = () => {
    if (typeof onSaved === 'function') onSaved();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBlock((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSavedNotice(null);
    try {
      const payload = {
        name: blockData.name,
        description: blockData.description,
        day_pattern: blockData.day_pattern,
        start_time: blockData.start_time,
        end_time: blockData.end_time,
        play_mode: blockData.play_mode,
        is_active: blockData.is_active,
      };

      if (blockId) {
        await api.put(`/api/rotation-blocks/${blockId}`, payload);
        setSavedNotice('Bloque guardado');
        notifySaved();
      } else {
        const { data } = await api.post('/api/rotation-blocks', payload);
        setBlockId(data.id);
        setSavedNotice('Bloque creado. Ya podés añadir shows a la rotación.');
        notifySaved();
      }
    } catch (err) {
      console.error('[RotationBlockEditor] Save failed:', err);
      setError(err.response?.data?.error || 'No se pudo guardar el bloque');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!blockId) return;
    try {
      await api.delete(`/api/rotation-blocks/${blockId}`);
      notifySaved();
      close();
    } catch (err) {
      console.error('[RotationBlockEditor] Delete failed:', err);
      setError(err.response?.data?.error || 'No se pudo eliminar el bloque');
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleAddShow = async (e) => {
    e.preventDefault();
    if (!blockId || !selectedShowId) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/rotation-blocks/${blockId}/shows`, {
        show_id: selectedShowId,
        weight: showWeight,
      });
      await reloadBlockShows(blockId);
      setSelectedShowId('');
      setShowWeight(100);
      setShowAddModal(false);
      notifySaved();
    } catch (err) {
      console.error('[RotationBlockEditor] Add show failed:', err);
      setError(err.response?.data?.error || 'No se pudo añadir el show');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveShow = async (showId) => {
    if (!blockId || !showId) return;
    try {
      await api.delete(`/api/rotation-blocks/${blockId}/shows/${showId}`);
      await reloadBlockShows(blockId);
      notifySaved();
    } catch (err) {
      console.error('[RotationBlockEditor] Remove show failed:', err);
      setError(err.response?.data?.error || 'No se pudo quitar el show');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <p className="text-dark-400">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">
            {blockId ? 'Editar Bloque de Rotación' : 'Crear Nuevo Bloque'}
          </h1>
          <p className="text-sm text-dark-400 mt-1">
            Define franjas horarias con shows que se reproducirán aleatoriamente
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" onClick={close} className="btn btn-secondary btn-sm">
            Volver
          </button>
          <button
            type="submit"
            form="rotation-block-form"
            disabled={saving}
            className="btn btn-primary btn-sm"
          >
            {saving ? 'Guardando...' : 'Guardar Bloque'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-error-900/40 border border-error-700 px-4 py-3 text-sm text-error-200">
          {error}
        </div>
      )}
      {savedNotice && !error && (
        <div className="rounded-lg bg-success-900/30 border border-success-700 px-4 py-3 text-sm text-success-200">
          {savedNotice}
        </div>
      )}

      <form id="rotation-block-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Nombre</label>
          <input
            type="text"
            name="name"
            value={blockData.name}
            onChange={handleChange}
            className="input input-bordered w-full"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Descripción</label>
          <textarea
            name="description"
            value={blockData.description}
            onChange={handleChange}
            className="textarea textarea-bordered w-full min-h-[80px]"
            placeholder="Descripción opcional del bloque"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Patrón de días</label>
            <select
              name="day_pattern"
              value={blockData.day_pattern}
              onChange={handleChange}
              className="select select-bordered w-full"
            >
              {PATTERN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Modo de reproducción</label>
            <select
              name="play_mode"
              value={blockData.play_mode}
              onChange={handleChange}
              className="select select-bordered w-full"
            >
              {PLAY_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Hora de inicio</label>
            <TimeSelect
              value={blockData.start_time}
              onChange={(v) => setBlock((prev) => ({ ...prev, start_time: v }))}
              className="input input-bordered w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Hora de fin</label>
            <TimeSelect
              value={blockData.end_time}
              onChange={(v) => setBlock((prev) => ({ ...prev, end_time: v }))}
              className="input input-bordered w-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <input
            type="checkbox"
            name="is_active"
            checked={blockData.is_active}
            onChange={handleChange}
            className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
          />
          <label htmlFor="is_active" className="text-sm text-dark-300">
            Activo
          </label>
        </div>
      </form>

      {/* Shows Management Section */}
      {blockId ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-dark-100">
              Shows en Rotación ({(shows || []).length})
            </h2>
            <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-outline btn-sm">
              + Añadir Show
            </button>
          </div>

          {(shows || []).length > 0 ? (
            <div className="space-y-2">
              {shows.map((show) => (
                <div
                  key={show.show_id || show.id}
                  className="flex items-center justify-between rounded-lg bg-dark-800/50 px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-dark-800 border border-dark-700">
                      {show.image_url ? (
                        <img src={show.image_url} alt={show.show_name || show.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="text-dark-400">📻</div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-dark-100">{show.show_name || show.name}</p>
                      <p className="text-xs text-dark-500">
                        {show.show_type} • {show.duration_minutes} min
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-dark-400">Peso: {show.weight || 100}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveShow(show.show_id || show.id)}
                      className="btn btn-error btn-xs"
                    >
                      −
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-dark-400 text-center py-4">
              No hay shows asignados a este bloque. Usa el botón "+ Añadir Show" para agregarlos.
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-dark-400">
          Guardá el bloque primero para poder asignarle shows.
        </p>
      )}

      {/* Add Show Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-dark-800 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Añadir Show al Bloque</h3>
            <form onSubmit={handleAddShow} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Show</label>
                <select
                  value={selectedShowId}
                  onChange={(e) => setSelectedShowId(e.target.value)}
                  className="select select-bordered w-full"
                  required
                >
                  <option value="">Seleccionar un show...</option>
                  {availableShows.map((show) => (
                    <option key={show.id} value={show.id}>
                      {show.name} ({show.show_type})
                    </option>
                  ))}
                </select>
                {availableShows.length === 0 && (
                  <p className="text-xs text-dark-500 mt-1">
                    No quedan shows activos disponibles para añadir.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Peso</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={showWeight}
                  onChange={(e) => setShowWeight(parseInt(e.target.value) || 100)}
                  className="input input-bordered w-full"
                  placeholder="100"
                />
                <p className="text-xs text-dark-500 mt-1">
                  Mayor peso = mayor probabilidad de selección (solo para modo ponderado)
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancelar
                </button>
                <button type="submit" disabled={!selectedShowId || saving} className="btn btn-primary btn-sm">
                  {saving ? 'Añadiendo...' : 'Añadir Show'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {blockId && (
        <>
          <div className="flex justify-end pt-4 border-t border-dark-700">
            <button type="button" onClick={() => setConfirmOpen(true)} className="btn btn-error btn-sm">
              Eliminar bloque
            </button>
          </div>
          <ConfirmDialog
            open={confirmOpen}
            title="Eliminar Bloque de Rotación"
            message="¿Seguro que quieres eliminar este bloque de rotación? Esta acción no se puede deshacer."
            danger
            onConfirm={confirmDelete}
            onClose={() => setConfirmOpen(false)}
          />
        </>
      )}
    </div>
  );
}
